import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@progress/kendo-react-buttons";
import { Typography } from "@progress/kendo-react-common";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import ShadowCard from "../../components/common/ShadowCard";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import FormTextField from "../../components/formFields/FormTextField";
import { getAllActiveCities } from "../city/services/city.services";
import { ICity } from "../city/cityModel";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { STATUSARRAY } from "../../_contstants/common";
import {
  createVehicle,
  getVehicleByID,
  updateVehicle,
} from "./services/vehicle.service";
import { clearVehicleDetails } from "./vehicleSlice";
import { IVehicleType } from "../vehicletype/vehicletypeModel";
import { getAllActiveVehicleTypes } from "../vehicletype/services/vehicletype.services";

const CreateVehicle: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const VehicleID = location.state?.VehicleID;
  const VehicleDetail = useAppSelector((state) => state.vehicle.VehicleDetail);
  const loading = useAppSelector((state) => state.vehicle.loading);
  const CityList = useAppSelector((state) => state.city.CityList);
  const VehicleTypeList = useAppSelector(
    (state) => state.vehicleType.VehicleTypeList,
  );
  const [formKey, setFormKey] = useState(1);

  useEffect(() => {
    if (VehicleID) {
      dispatch(getVehicleByID(VehicleID));
    }
  }, [VehicleID]);

  useEffect(() => {
    dispatch(getAllActiveCities());
    dispatch(getAllActiveVehicleTypes());

    return () => {
      dispatch(clearVehicleDetails());
    };
  }, []);

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [VehicleDetail, CityList]);

  const handleSubmit = async (values: any) => {
    try {
      if (VehicleID) {
        const editPayload = {
          ID: VehicleID,
          CityID: values.CityID ? +values.CityID : null,
          VehicleTypeID: values.VehicleTypeID ? +values.VehicleTypeID : null,
          NoOfSeats: values.NoOfSeats ? +values.NoOfSeats : null,
          KmPerDay: values.KmPerDay ? +values.KmPerDay : null,
          RatePerKm: values.RatePerKm ? +values.RatePerKm : null,
          DriverAllowancePerDay: values.DriverAllowancePerDay
            ? +values.DriverAllowancePerDay
            : null,
          PickUpWithinCity: values.PickUpWithinCity
            ? +values.PickUpWithinCity
            : null,
          DropWithinCity: values.DropWithinCity ? +values.DropWithinCity : null,
          EightHrEightyKmWithinCity: values.EightHrEightyKmWithinCity
            ? +values.EightHrEightyKmWithinCity
            : null,
          IsActive: values.IsActive === 1,
        };
        const response = await dispatch(updateVehicle(editPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          navigate(-1);
        }
      } else {
        const insertPayload = {
          CityID: values.CityID ? +values.CityID : null,
          VehicleTypeID: values.VehicleTypeID ? +values.VehicleTypeID : null,
          NoOfSeats: values.NoOfSeats ? +values.NoOfSeats : null,
          KmPerDay: values.KmPerDay ? +values.KmPerDay : null,
          RatePerKm: values.RatePerKm ? +values.RatePerKm : null,
          DriverAllowancePerDay: values.DriverAllowancePerDay
            ? +values.DriverAllowancePerDay
            : null,
          PickUpWithinCity: values.PickUpWithinCity
            ? +values.PickUpWithinCity
            : null,
          DropWithinCity: values.DropWithinCity ? +values.DropWithinCity : null,
          EightHrEightyKmWithinCity: values.EightHrEightyKmWithinCity
            ? +values.EightHrEightyKmWithinCity
            : null,
          IsActive: values.IsActive === 1,
        };
        const response = await dispatch(createVehicle(insertPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          navigate("/vehicle");
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <Form
        key={formKey}
        onSubmit={handleSubmit}
        initialValues={VehicleDetail}
        render={(formRenderProps: FormRenderProps) => (
          <FormElement>
            <ShadowCard style={{ padding: 12 }}>
              <GridLayout
                style={{ marginRight: 20 }}
                gap={{ rows: 5, cols: 10 }}
                cols={[
                  { width: "33.33%" },
                  { width: "33.33%" },
                  { width: "33.33%" },
                ]}
              >
                <GridLayoutItem colSpan={3}>
                  <Typography.h4>
                    {VehicleID ? "Update Vehicle" : "Add Vehicle"}
                  </Typography.h4>
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    id={"CityID"}
                    name={"CityID"}
                    label={"City"}
                    placeholder="i.e. Ahmedabad"
                    component={FormSelectionField}
                    validator={requiredValidator}
                    astrike={true}
                    options={CityList?.map((city: ICity) => {
                      return {
                        value: city?.ID,
                        label: city?.CityName,
                      };
                    })}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    id={"VehicleTypeID"}
                    name={"VehicleTypeID"}
                    label={"Vehicle Type"}
                    placeholder="Vehicle Type"
                    component={FormSelectionField}
                    validator={requiredValidator}
                    astrike={true}
                    options={VehicleTypeList?.map(
                      (vehicletype: IVehicleType) => {
                        return {
                          value: vehicletype?.ID,
                          label: vehicletype?.VehicleType,
                        };
                      },
                    )}
                  />
                </GridLayoutItem>

                <GridLayoutItem>
                  <Field
                    wrapperClassName="right-aligned-field"
                    name="NoOfSeats"
                    label="No Of Seats"
                    placeholder="0"
                    type="number"
                    component={FormTextField}
                    validator={requiredValidator}
                    astrike={true}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    wrapperClassName="right-aligned-field"
                    name="KmPerDay"
                    label="Km Per Day "
                    placeholder="0"
                    type="number"
                    component={FormTextField}
                    validator={requiredValidator}
                    astrike={true}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    wrapperClassName="right-aligned-field"
                    name="RatePerKm"
                    label="Rate Per Km "
                    placeholder="0"
                    type="number"
                    component={FormTextField}
                    validator={requiredValidator}
                    astrike={true}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    wrapperClassName="right-aligned-field"
                    name="DriverAllowancePerDay"
                    label="Driver Allowance Per Day"
                    placeholder="0"
                    type="number"
                    component={FormTextField}
                    validator={requiredValidator}
                    astrike={true}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    wrapperClassName="right-aligned-field"
                    name="PickUpWithinCity"
                    label="PickUp Within City"
                    placeholder="0"
                    type="number"
                    component={FormTextField}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    wrapperClassName="right-aligned-field"
                    name="DropWithinCity"
                    label="Drop Within City"
                    placeholder="0"
                    type="number"
                    component={FormTextField}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    wrapperClassName="right-aligned-field"
                    name="EightHrEightyKmWithinCity"
                    label="Eight Hr Eighty Km Within City"
                    placeholder="0"
                    type="number"
                    component={FormTextField}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name={`IsActive`}
                    label="Status"
                    component={FormSelectionField}
                    astrike={true}
                    validator={requiredValidator}
                    placeholder="i.e. Active"
                    options={STATUSARRAY?.map((status: any) => {
                      return {
                        value: status?.value,
                        label: status?.label,
                      };
                    })}
                  />
                </GridLayoutItem>
                <GridLayoutItem
                  colSpan={2}
                  style={{
                    marginTop: "20px",
                    display: "flex",
                    justifyContent: "end",
                    alignItems: "end",
                  }}
                >
                  <div>
                    <ButtonWithLoading
                      label={VehicleID ? "Update" : "Save"}
                      type="submit"
                      disabled={!formRenderProps.allowSubmit || loading}
                      loading={loading}
                    />
                    <Button
                      type="button"
                      fillMode={"outline"}
                      themeColor={"primary"}
                      style={{ marginLeft: 10 }}
                      onClick={() => navigate("/vehicle")}
                    >
                      Cancel
                    </Button>
                  </div>
                </GridLayoutItem>
              </GridLayout>
            </ShadowCard>
          </FormElement>
        )}
      />
    </>
  );
};

export default CreateVehicle;
